<template>
  <div v-show="showPigRunManager">
    <q-card
      v-show="!wizardVisible"
      square
      flat
      bordered
      class="my-card bg-grey-1 vertical-bottom"
    >
      <q-toolbar>
        <q-toolbar-title class="text-weight-bold text-grey-brand"
          >Pig Run Manager</q-toolbar-title
        >
        <q-btn flat round dense icon="close" @click="onCancelPigRun" />
      </q-toolbar>
      <q-card-section>
        <div class="column">
          <q-btn
            v-if="userHasAnyRole('admin') && !userHasAnyRole('superadmin')"
            flat
            rounded
            class="btn-brand"
            icon="fas fa-plus"
            label="New Pig Run"
            no-caps
            @click="onNewPigRun"
          />
          <br />
          <q-separator inset />
          <br />
          <q-expansion-item
            v-if="pigRunOptions.length > 0"
            v-model="pigRunSectionExpanded"
            dense
            header-class="text-overline text-grey-brand"
            label="Pig Runs"
          >
            <q-select
              v-model="selectedPigRun"
              dense
              color="blue-brand"
              outlined
              clearable
              :use-input="!selectedPigRun"
              input-debounce="300"
              :options="filteredPigRunOptions"
              label="Select Pig Run"
              :display-value="
                selectedPigRun ? truncateLabel(selectedPigRun.label) : ''
              "
              @filter="filterPigRuns"
              @update:model-value="onPreviewPigRun"
              @clear="onClearPigRun"
            />
            <br />
            <div class="col q-pb-sm">
              <q-btn
                flat
                rounded
                class="btn-brand"
                icon="file_download"
                label="Export CSV"
                no-caps
                :disable="selectedPigRun == ''"
                :loading="isExportingCsv"
                @click="onExportPigRun"
              />
            </div>
            <div class="col q-pb-sm">
              <q-btn
                flat
                rounded
                class="btn-brand"
                icon="fas fa-edit"
                label="Edit"
                no-caps
                :disable="selectedPigRun == ''"
                @click="onEditPigRun"
              />
            </div>
            <div class="col q-pb-sm">
              <q-btn
                flat
                rounded
                class="btn-brand"
                icon="fas fa-trash-alt"
                label="Delete"
                no-caps
                :disable="selectedPigRun == ''"
                @click="onDeletePigRun"
              />
            </div>
            <div v-if="selectedPigRunType === 'LT'" class="col q-pb-sm">
              <q-btn
                flat
                rounded
                class="btn-brand"
                icon="fas fa-rocket"
                label="Launch Pig"
                no-caps
                :disable="selectedPigRun == ''"
                @click="onLaunchPigRun"
              />
            </div>
            <div v-if="selectedPigRunType === 'R'" class="col q-pb-sm">
              <q-btn
                flat
                rounded
                class="btn-brand"
                icon="fas fa-history"
                label="Reconstruct"
                no-caps
                :disable="selectedPigRun == ''"
                @click="onWizardReconstruct"
              />
            </div>
          </q-expansion-item>
          <br />
          <q-separator inset />
          <br />

          <q-expansion-item
            v-if="completedPigRuns.length > 0"
            v-model="completedRunSectionExpanded"
            dense
            header-class="text-overline text-grey-brand"
            label="Completed Run Reports"
          >
            <q-select
              v-model="selectedPigReport"
              dense
              color="blue-brand"
              outlined
              clearable
              :use-input="!selectedPigReport"
              input-debounce="300"
              :options="filteredCompletedRunOptions"
              label="Completed Run"
              :display-value="
                selectedPigReport ? truncateLabel(selectedPigReport.label) : ''
              "
              @filter="filterCompletedRuns"
              @update:model-value="onPreviewCompletedPigRun"
              @clear="onClearCompletedPigRun"
            />
            <PigRunSummary
              v-if="selectedCompletedPigRunId"
              ref="pigRunSummary"
              :pig-run-id="selectedCompletedPigRunId"
              skip-plot
              class="q-mt-sm"
            />
            <br />
            <div class="col q-pb-sm">
              <q-btn
                v-close-popup
                flat
                rounded
                no-caps
                icon="fas fa-trash-alt"
                class="btn-brand"
                label="Delete Run"
                :disable="selectedPigReport == ''"
                @click="onDeleteReport"
              />
            </div>
            <div class="col q-pb-sm">
              <q-btn
                v-close-popup
                flat
                rounded
                no-caps
                icon="edit"
                class="btn-brand"
                label="Edit Time"
                :disable="selectedPigReport == ''"
                @click="onEditTime"
              />
            </div>
            <div class="col q-pb-sm">
              <q-btn
                v-close-popup
                flat
                rounded
                no-caps
                icon="add"
                class="btn-brand"
                label="Add AGMs"
                :disable="selectedPigReport == ''"
                @click="onAddAgmsDialog"
              />
            </div>
            <div class="col q-pb-sm">
              <q-btn
                v-close-popup
                flat
                rounded
                :loading="isRCompRunSynching"
                no-caps
                icon="refresh"
                class="btn-brand"
                label="Synch Run"
                :disable="selectedPigReport == ''"
                @click="onSynchRun"
              />
            </div>
            <div class="col q-pb-sm">
              <q-btn
                flat
                rounded
                no-caps
                icon="fas fa-broadcast-tower"
                class="btn-brand"
                label="Broadcast Command"
                :disable="completedPigRunBroadcastCommandButtonDisable"
                @click="completedPigRunBroadcastCommandButtonOnClick"
              />
            </div>
            <div class="col q-pb-sm">
              <q-btn
                v-close-popup
                flat
                rounded
                no-caps
                icon="assessment"
                class="btn-brand"
                label="View Report"
                :disable="selectedPigReport == ''"
                @click="onOpenReportForCompletedRun"
              />
            </div>
          </q-expansion-item>

          <q-dialog v-model="addAgmsDialog">
            <q-card style="width: 400px">
              <q-toolbar>
                <div class="text-blue-brand text-bold">Add AGMs</div>
                <q-space />
                <q-btn
                  v-close-popup
                  flat
                  round
                  dense
                  icon="close"
                  @click="onAgmListCancelled"
                />
              </q-toolbar>
              <q-card-section>
                <div class="row items-center q-gutter-sm q-mb-sm">
                  <q-input
                    v-model="addAgmSearch"
                    dense
                    outlined
                    color="blue-brand"
                    label="Search"
                    clearable
                    style="flex: 1"
                  >
                    <template #prepend>
                      <q-icon name="search" />
                    </template>
                  </q-input>
                </div>
                <div class="row items-center q-gutter-xs q-mb-sm">
                  <q-input
                    v-model="addAgmRangeFrom"
                    dense
                    outlined
                    type="number"
                    label="From"
                    style="max-width: 80px"
                    color="blue-brand"
                  />
                  <q-input
                    v-model="addAgmRangeTo"
                    dense
                    outlined
                    type="number"
                    label="To"
                    style="max-width: 80px"
                    color="blue-brand"
                  />
                  <q-btn
                    flat
                    dense
                    no-caps
                    color="blue-brand"
                    label="Select Range"
                    :disable="!addAgmRangeFrom || !addAgmRangeTo"
                    @click="onAddAgmSelectRange"
                  />
                  <q-space />
                  <q-btn
                    flat
                    dense
                    no-caps
                    color="blue-brand"
                    :label="allAddAgmsSelected ? 'Deselect All' : 'Select All'"
                    @click="onToggleSelectAllAddAgms"
                  />
                </div>
                <q-list
                  ref="addAgmList"
                  bordered
                  separator
                  class="rounded-borders"
                  style="max-height: 350px; overflow-y: auto"
                >
                  <q-item
                    v-for="agm in filteredAddAgmOptions"
                    :key="agm.value"
                    tag="label"
                    dense
                  >
                    <q-item-section side>
                      <q-checkbox
                        v-model="addAgmSelected"
                        :val="agm.value"
                        color="blue-brand"
                      />
                    </q-item-section>
                    <q-item-section>{{ agm.label }}</q-item-section>
                  </q-item>
                  <q-item v-if="filteredAddAgmOptions.length === 0">
                    <q-item-section class="text-grey-6 text-center">
                      No AGMs available to add
                    </q-item-section>
                  </q-item>
                </q-list>
                <q-separator class="q-mt-md" />
                <q-card-actions align="center">
                  <q-btn
                    v-close-popup
                    class="btn-brand"
                    label="Cancel"
                    no-caps
                    @click="onAgmListCancelled"
                  />
                  <q-btn
                    v-close-popup
                    class="btn-brand"
                    label="Save Selected"
                    no-caps
                    :disable="addAgmSelected.length === 0"
                    @click="onAgmListChanged"
                  />
                </q-card-actions>
              </q-card-section>
            </q-card>
          </q-dialog>
        </div>
      </q-card-section>
    </q-card>

    <q-dialog v-model="EditTimeDialog">
      <q-card>
        <q-toolbar>
          <div class="text-blue-brand text-bold">Edit Launch/Receive Time</div>
          <q-space />
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section>
          <q-form v-if="ErrorMsg">
            <q-badge class="q-ml-sm" color="red">
              {{ ErrorMsgString }}
            </q-badge>
          </q-form>
          <q-form>
            <div class="column">
              <div class="col q-pb-sm">
                <q-input
                  v-model="LaunchCompTimestamp"
                  dense
                  filled
                  label="Launch Time (UTC)"
                >
                  <template #prepend>
                    <q-icon name="event" class="cursor-pointer">
                      <q-popup-proxy
                        ref="qDateProxy"
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-date
                          v-model="LaunchCompTimestamp"
                          mask="YYYY-MM-DD HH:mm:ss"
                          @input="hideDateProxy"
                        />
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                  <template #append>
                    <q-icon name="access_time" class="cursor-pointer">
                      <q-popup-proxy
                        ref="qTimeProxy"
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-time
                          v-model="LaunchCompTimestamp"
                          mask="YYYY-MM-DD HH:mm:ss"
                          format24h
                          @input="hideTimeProxy"
                        />
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                </q-input>
              </div>
            </div>
            <q-separator />
            <div class="column">
              <div class="col q-pb-sm">
                <q-input
                  v-model="ReceiveCompTimestamp"
                  dense
                  filled
                  label="Receive Time (UTC)"
                >
                  <template #prepend>
                    <q-icon name="event" class="cursor-pointer">
                      <q-popup-proxy
                        ref="qDateProxy"
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-date
                          v-model="ReceiveCompTimestamp"
                          mask="YYYY-MM-DD HH:mm:ss"
                          @input="hideDateProxy"
                        />
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                  <template #append>
                    <q-icon name="access_time" class="cursor-pointer">
                      <q-popup-proxy
                        ref="qTimeProxy"
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-time
                          v-model="ReceiveCompTimestamp"
                          mask="YYYY-MM-DD HH:mm:ss"
                          format24h
                          @input="hideTimeProxy"
                        />
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                </q-input>
              </div>
            </div>

            <q-card-actions align="center">
              <q-btn v-close-popup no-caps class="btn-brand" label="Cancel" />
              <q-btn
                no-caps
                class="btn-brand"
                label="Update"
                :disable="isUpdatingCompletedRunTime"
                :loading="isUpdatingCompletedRunTime"
                @click="onEditCompTime"
              />
            </q-card-actions>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <q-dialog v-model="launchPigDialog">
      <q-card style="width: 400px">
        <q-toolbar>
          <div class="text-blue-brand text-bold">Launch the Pig</div>
          <q-space />
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section>
          <div class="column">
            <div class="col q-pb-sm">
              <q-input
                v-model="launchPigToolName"
                dense
                color="blue-brand"
                type="text"
                outlined
                label="Tool Name"
                maxlength="100"
                :rules="[toolNameRule]"
                reactive-rules
                hide-bottom-space
                @update:model-value="onReadyLaunch"
              />
            </div>
            <div class="col q-pb-sm">
              <q-select
                v-model="launchPigToolType"
                dense
                color="blue-brand"
                outlined
                :options="launchPigToolTypeOptions"
                label="Select Tool Type"
                emit-value
                map-options
                @update:model-value="onReadyLaunch"
              />
            </div>
            <div class="col q-pb-sm">
              <q-input
                v-model="LaunchTimestamp"
                dense
                outlined
                label="Launch Time (UTC)"
              >
                <template #prepend>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qDateProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-date
                        v-model="LaunchTimestamp"
                        mask="YYYY-MM-DD HH:mm:ss"
                        @input="hideDateProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
                <template #append>
                  <q-icon name="access_time" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qTimeProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-time
                        v-model="LaunchTimestamp"
                        mask="YYYY-MM-DD HH:mm:ss"
                        format24h
                        with-seconds
                        @input="hideTimeProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </div>
            <div class="col q-pb-sm">
              <q-btn
                flat
                dense
                no-caps
                color="grey-7"
                :icon="
                  showAdvancedLaunchOptions ? 'expand_less' : 'expand_more'
                "
                :label="advancedLaunchOptionsLabel"
                @click="showAdvancedLaunchOptions = !showAdvancedLaunchOptions"
              />
            </div>
            <div v-if="showAdvancedLaunchOptions" class="col q-pb-sm">
              <div class="q-pb-sm">
                <q-checkbox
                  v-model="directionIsBackward"
                  dense
                  color="blue-brand"
                  label="Run Backwards"
                  @update:model-value="onRunBackwardsChanged"
                >
                  <q-tooltip
                    anchor="top middle"
                    self="bottom middle"
                    :offset="[0, 8]"
                  >
                    This is for bidirectional Pigruns where the launch and
                    recieve locations can be swapped.
                  </q-tooltip>
                </q-checkbox>
              </div>
              <div class="row items-start q-gutter-sm">
                <q-input
                  v-model.number="launchPigExpectedSpeed"
                  dense
                  color="blue-brand"
                  type="number"
                  outlined
                  :label="expectedSpeedLabel"
                  hint="Override the default speed"
                  clearable
                  class="col"
                  @clear="launchPigExpectedSpeed = null"
                />
                <q-select
                  v-model="launchPigSpeedUnit"
                  dense
                  outlined
                  color="blue-brand"
                  :options="[
                    { label: 'mph', value: 'mph' },
                    { label: 'km/h', value: 'km/h' },
                  ]"
                  emit-value
                  map-options
                  style="width: 90px"
                />
              </div>
            </div>
            <q-badge v-if="ErrorMsg" class="q-ml-sm q-mb-sm" color="red">
              {{ ErrorMsgString }}
            </q-badge>
          </div>
        </q-card-section>
        <q-card-actions align="center">
          <q-btn v-close-popup no-caps class="btn-brand" label="Cancel" />
          <q-btn
            no-caps
            icon="fas fa-rocket"
            class="btn-brand"
            :disabled="disableLaunch"
            label="Launch"
            @click="onAddLaunchTime"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <BackwardsConfirmDialog
      v-model="showBackwardsConfirm"
      :pig-run-id="selectedPigRun ? selectedPigRun.value : 0"
      :markers="previewSortedMarkers"
      @confirmed="onBackwardsConfirmed"
      @cancelled="onBackwardsCancelled"
    />

    <!-- Complete pig run broadcast command dialog definition. -->
    <q-dialog v-model="displayCompletedPigRunBroadcastCommandDialog">
      <q-card style="width: 600px">
        <q-toolbar>
          <q-toolbar-title class="text-blue-brand text-bold"
            >Completed Pig Run Broadcast Commands</q-toolbar-title
          >
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section>
          <q-markup-table>
            <thead>
              <tr>
                <th class="text-left">
                  <span class="text-bold">Command</span>
                </th>
                <th class="text-right">
                  <span class="text-bold q-pr-sm">Now</span>
                  <q-icon name="fas fa-info-circle">
                    <q-tooltip>Commands to be sent to AGMs now.</q-tooltip>
                  </q-icon>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-left">Download between launch and receive</td>
                <td class="text-right">
                  <q-checkbox
                    v-model="
                      completedPigRunBroadcastCommandDownloadBetweenLaunchAndReceive
                    "
                    color="blue-brand"
                  ></q-checkbox>
                </td>
              </tr>
            </tbody>
          </q-markup-table>
        </q-card-section>
        <q-separator></q-separator>
        <q-card-actions align="right">
          <q-btn
            class="btn-brand"
            label="Broadcast Commands Now"
            no-caps
            @click="completedPigRunBroadcastCommandBroadcastNowOnClick"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Reconstruct Pig Run Dialog -->
    <q-dialog v-model="reconstructDialog">
      <q-card style="width: 400px">
        <q-toolbar>
          <div class="text-blue-brand text-bold">Reconstruct Pig Run</div>
          <q-space />
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section>
          <div class="q-mb-sm text-grey-8">
            Enter the launch and receive dates for the selected pig run. The
            system will find matching passage detections and associate them with
            the pipeline markers.
          </div>
          <div class="column">
            <div class="col q-pb-sm">
              <q-input
                v-model="reconstructLaunchDate"
                dense
                filled
                label="Launch Date (UTC)"
              >
                <template #prepend>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy
                      ref="reconstructLaunchDateProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-date
                        v-model="reconstructLaunchDate"
                        mask="YYYY-MM-DD HH:mm:ss"
                        @input="hideReconstructLaunchDateProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
                <template #append>
                  <q-icon name="access_time" class="cursor-pointer">
                    <q-popup-proxy
                      ref="reconstructLaunchTimeProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-time
                        v-model="reconstructLaunchDate"
                        mask="YYYY-MM-DD HH:mm:ss"
                        format24h
                        @input="hideReconstructLaunchTimeProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </div>
            <div class="col q-pb-sm">
              <q-input
                v-model="reconstructReceiveDate"
                dense
                filled
                label="Receive Date (UTC)"
              >
                <template #prepend>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy
                      ref="reconstructReceiveDateProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-date
                        v-model="reconstructReceiveDate"
                        mask="YYYY-MM-DD HH:mm:ss"
                        @input="hideReconstructReceiveDateProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
                <template #append>
                  <q-icon name="access_time" class="cursor-pointer">
                    <q-popup-proxy
                      ref="reconstructReceiveTimeProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-time
                        v-model="reconstructReceiveDate"
                        mask="YYYY-MM-DD HH:mm:ss"
                        format24h
                        @input="hideReconstructReceiveTimeProxy"
                      />
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </div>
          </div>
        </q-card-section>
        <q-card-actions align="center">
          <q-btn v-close-popup no-caps class="btn-brand" label="Cancel" />
          <q-btn
            no-caps
            class="btn-brand"
            label="Reconstruct"
            :loading="isReconstructing"
            :disable="!reconstructLaunchDate || !reconstructReceiveDate"
            @click="onReconstructConfirm"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <PigRunWizard
      ref="pigRunWizard"
      @saved="onWizardSaved"
      @visibility-change="wizardVisible = $event"
    />

    <!-- Launch marker TRACK state prompt + passage time dialog -->
    <q-dialog v-model="launchMarkerPassagePromptDialog" persistent>
      <q-card style="width: 450px">
        <q-toolbar>
          <div class="text-blue-brand text-bold">Add Launch Passage?</div>
          <q-space />
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section>
          <p>
            The launch site has a marker in the TRACK state. The pig icon will
            not move until a passage at this marker is verified. Do you want to
            add a manual passage at the launch marker?
          </p>
          <div class="col q-pb-sm">
            <q-input
              v-model="launchMarkerPassageTimestamp"
              dense
              outlined
              label="Passage Time (UTC)"
            >
              <template #prepend>
                <q-icon name="event" class="cursor-pointer">
                  <q-popup-proxy
                    ref="launchPassageDateProxy"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="launchMarkerPassageTimestamp"
                      mask="YYYY-MM-DD HH:mm:ss"
                      @input="hideLaunchPassageDateProxy"
                    />
                  </q-popup-proxy>
                </q-icon>
              </template>
              <template #append>
                <q-icon name="access_time" class="cursor-pointer">
                  <q-popup-proxy
                    ref="launchPassageTimeProxy"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-time
                      v-model="launchMarkerPassageTimestamp"
                      mask="YYYY-MM-DD HH:mm:ss"
                      format24h
                      with-seconds
                      @input="hideLaunchPassageTimeProxy"
                    />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <q-badge
            v-if="launchMarkerPassageErrorMsg"
            class="q-ml-sm q-mb-sm"
            color="red"
          >
            {{ launchMarkerPassageErrorMsgString }}
          </q-badge>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup no-caps class="btn-brand" label="No" />
          <q-btn
            no-caps
            class="btn-brand"
            label="Yes"
            :loading="isAddingLaunchPassage"
            @click="onAddLaunchMarkerPassage"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <InstructionCard
      v-model="showInstructions"
      title="Getting Started"
      icon="route"
      message="Pig runs are built on top of pipelines. Before creating a pig run, set up your pipeline in the Pipeline Manager."
      action-label="Open Pipeline Manager"
      action-icon="settings"
      @action="onOpenPipelineSectionManager"
    />

    <PipelineSectionManager v-model="managePipelineSectionDialog" />
  </div>
</template>

<script lang="ts">
import { defineComponent, markRaw } from 'vue';
import L from 'leaflet';
import * as dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { hidePopupProxy } from '@/utils/QuasarProxyHelper';
import { Device } from '@/models/Device';
import { CompletedPigRunBroadcastCommand } from '@/models/CompletedPigRunBroadcastCommand';
import { ReconstructPigRunRequest } from '@/models/ReconstructPigRunRequest';
import { LaunchLiveTrackPigRunRequest } from '@/models/LaunchLiveTrackPigRunRequest';
import { LaunchPigRunPayload } from '@/models/PigRunState';
import { AddManualPassageRequest } from '@/models/ReportManager/AddManualPassageRequest';
import PigRunService from '@/services/PigRunService';
import { ActivePigRun } from '@/models/ActivePigRun';
import PigRunWizard from '@/components/PigRunWizard.vue';
import PipelineSectionManager from '@/components/PipelineSectionManager.vue';
import InstructionCard from '@/components/InstructionCard.vue';
import PigRunSummary from '@/components/PigRunSummary.vue';
import BackwardsConfirmDialog from '@/components/BackwardsConfirmDialog.vue';
import PigRunCommon from '@/utils/PigRunCommon';
import { hasAnyRole } from '@/utils/AuthGuard';
import { escapeHtml } from '@/utils/HtmlEscape';
import {
  buildPipelineImportCsv,
  sanitizeCsvFilename,
  triggerCsvDownload,
} from '@/utils/PipelineCsvExport';
dayjs.extend(utc);

export default defineComponent({
  name: 'PigRunManager',
  components: {
    PigRunWizard,
    PipelineSectionManager,
    InstructionCard,
    PigRunSummary,
    BackwardsConfirmDialog,
  },
  data() {
    return {
      selectedPigRun: '' as any,
      selectedPigReport: '' as any,
      pigRunPreview: null as L.Polyline | null,
      kmlPreview: null as L.Polyline | null,
      launchPreview: null as L.Marker | null,
      recievePreview: null as L.Marker | null,
      offlinePreviews: {} as { [key: string]: L.Marker },
      offlineAgms: {} as Record<string, [number, number]>,
      agmMapMarkers: [] as L.Marker[],
      previewCircles: [] as L.Circle[],
      previewGeneration: 0,
      addAgmsDialog: false,
      addAgmOptions: [] as { label: string; value: number }[],
      addAgmSelected: [] as number[],
      addAgmSearch: '',
      addAgmRangeFrom: '',
      addAgmRangeTo: '',
      wizardVisible: false,
      directionIsBackward: false,
      showBackwardsConfirm: false,
      previewSortedMarkers: [] as any[],
      completedPigRunBroadcastCommandButtonDisableLocal: true,
      displayCompletedPigRunBroadcastCommandDialog: false,
      completedPigRunBroadcastCommandDownloadBetweenLaunchAndReceive: false,
      launchPigDialog: false,
      disableLaunchLocal: true,
      selectedPig: '' as any,
      pigOptionsLocal: [] as any[],
      Launch_at_TimeDialog: false,
      LaunchTimestamp: '',
      launchPigJobNumber: '' as any,
      launchPigToolName: '' as any,
      launchPigToolType: '' as any,
      launchPigToolTypeArray: [] as any[],
      showAdvancedLaunchOptions: false,
      launchPigExpectedSpeed: null as number | null,
      launchPigSpeedUnit: 'mph' as 'mph' | 'km/h',
      EditTimeDialog: false,
      ReceiveCompTimestamp: '',
      LaunchCompTimestamp: '',
      isUpdatingCompletedRunTime: false,
      reconstructDialog: false,
      reconstructLaunchDate: '',
      reconstructReceiveDate: '',
      isReconstructing: false,
      filteredCompletedRunOptions: [] as any[],
      filteredPigRunOptions: [] as any[],
      ErrorMsg: false,
      ErrorMsgString: '',

      unsubscribe: (() => {}) as () => void,
      showInstructions: false,
      managePipelineSectionDialog: false,
      pigRunsList: [] as ActivePigRun[],
      isPigRunsListLoading: false,
      isExportingCsv: false,
      launchMarkerPassagePromptDialog: false,
      pendingLaunchMarkerId: null as number | null,
      pendingLaunchPigRunId: null as number | null,
      launchMarkerPassageTimestamp: '',
      launchMarkerPassageErrorMsg: false,
      launchMarkerPassageErrorMsgString: '',
      isAddingLaunchPassage: false,
      pigRunSectionExpanded: true,
      completedRunSectionExpanded: true,
    };
  },
  computed: {
    showPigRunManager(): boolean {
      return this.$store.state.showPigRunManager;
    },
    user(): any {
      return this.$store.state.user;
    },
    map(): any {
      return this.$store.state.map;
    },
    pigRunOptions(): any[] {
      return this.pigRunsList
        .filter((pr: any) => pr.lifecycleState === 'C')
        .map((pr: any) => ({
          label: pr.name,
          value: pr.pigRunId,
          type: pr.type,
          runData: pr,
        }));
    },
    selectedPigRunType(): string | null {
      if (this.selectedPigRun && this.selectedPigRun.type) {
        return this.selectedPigRun.type;
      }
      return null;
    },
    launchPigToolTypeOptions(): any[] {
      return this.launchPigToolTypeArray;
    },
    completedPigRuns(): any[] {
      return this.pigRunsList.filter((pr: any) => pr.lifecycleState === 'D');
    },
    selectedCompletedPigRunId(): number | undefined {
      return this.selectedPigReport?.value ?? undefined;
    },
    completedRunOptions(): any[] {
      return this.completedPigRuns.map((pr: any) => {
        let dateLabel = 'N/A';
        if (pr.receiveDate) {
          const raw = String(pr.receiveDate).trim().replace(' ', 'T');
          const utcStr = raw.endsWith('Z') ? raw : raw + 'Z';
          dateLabel = dayjs.default(utcStr).utc().format('YYYY-MM-DD HH:mm');
        }
        return {
          value: pr.pigRunId,
          runData: pr,
          label: dateLabel + ' / ' + pr.name,
        };
      });
    },
    isRCompRunSynching(): boolean {
      return this.$store.state.pigRun.isRCompRunSynching;
    },
    activePigRuns(): any[] {
      return this.$store.getters.activePigRunsList;
    },
    completedPigRunBroadcastCommandButtonDisable(): boolean {
      return this.completedPigRunBroadcastCommandButtonDisableLocal;
    },
    pigOptions(): any[] {
      return this.pigOptionsLocal;
    },
    disableLaunch(): boolean {
      return this.disableLaunchLocal;
    },
    // EN-1849: Run Backwards lives inside the collapsed Advanced section, so
    // keep a visible reminder on the toggle while it is checked.
    advancedLaunchOptionsLabel(): string {
      if (this.showAdvancedLaunchOptions) return 'Hide Advanced';
      return this.directionIsBackward
        ? 'Advanced Options (Run Backwards on)'
        : 'Advanced Options';
    },
    expectedSpeedLabel(): string {
      return 'Expected Speed (' + this.launchPigSpeedUnit + ')';
    },
    filteredAddAgmOptions(): { label: string; value: number }[] {
      if (!this.addAgmSearch) return this.addAgmOptions;
      const q = this.addAgmSearch.toLowerCase();
      return this.addAgmOptions.filter((a) =>
        a.label.toLowerCase().includes(q)
      );
    },
    allAddAgmsSelected(): boolean {
      const visible = this.filteredAddAgmOptions;
      return (
        visible.length > 0 &&
        visible.every((a) => this.addAgmSelected.includes(a.value))
      );
    },
  },
  watch: {
    showPigRunManager(visible: boolean) {
      if (visible) {
        this.showInstructions = true;
        this.fetchPigRunsList();
      } else {
        this.showInstructions = false;
        this.removePreview();
      }
    },
  },
  created() {
    this.unsubscribe = this.$store.subscribeAction((action: any) => {
      if (action.type === 'ClearCompletedRun') {
        this.clearCompletedRun();
      }
      if (action.type === 'RefreshPigRunsList') {
        this.fetchPigRunsList();
      }
      if (action.type === 'verifyPassageComplete') {
        this.refreshPreviewAfterVerification();
      }
    });
  },
  async mounted() {
    await this.fetchPigRunsList();
  },
  beforeUnmount() {
    this.unsubscribe();
  },
  methods: {
    userHasAnyRole(...roles: string[]): boolean {
      return hasAnyRole(this.user, ...roles);
    },
    // --- Show / Hide / Cancel ---
    onCancelPigRun() {
      this.selectedPigRun = '';
      this.selectedPigReport = '';
      this.$store.commit('SET_SELECTED_COMPLETED_PIG_RUN_ID', null);
      this.directionIsBackward = false;
      this.removePreview();
      this.$store.dispatch('hidePigRunManager');
    },
    clearCompletedRun() {
      this.selectedPigReport = '';
      this.$store.commit('SET_SELECTED_COMPLETED_PIG_RUN_ID', null);
      this.removePreview();
    },

    // --- Map Preview Helpers ---
    removePreview() {
      this.previewGeneration++;
      this.$store.commit('SET_PLOTTED_PIG_RUN_ID', null);
      if (this.pigRunPreview) {
        this.pigRunPreview.removeFrom(this.map);
        this.pigRunPreview = null;
      }
      if (this.kmlPreview) {
        this.kmlPreview.removeFrom(this.map);
        this.kmlPreview = null;
      }
      if (this.launchPreview) {
        this.launchPreview.removeFrom(this.map);
        this.launchPreview = null;
      }
      if (this.recievePreview) {
        this.recievePreview.removeFrom(this.map);
        this.recievePreview = null;
      }
      if (this.offlinePreviews) {
        for (const uid in this.offlinePreviews) {
          if (Object.prototype.hasOwnProperty.call(this.offlinePreviews, uid)) {
            this.offlinePreviews[uid].removeFrom(this.map);
          }
        }
      }
      for (let i = 0; i < this.agmMapMarkers.length; i++) {
        this.agmMapMarkers[i].removeFrom(this.map);
      }
      this.agmMapMarkers = [];
      for (let i = 0; i < this.previewCircles.length; i++) {
        this.previewCircles[i].removeFrom(this.map);
      }
      this.previewCircles = [];
      this.previewSortedMarkers = [];
    },

    // --- Filter Helpers ---
    filterPigRuns(val: string, update: (fn: () => void) => void) {
      if (val === '') {
        update(() => {
          this.filteredPigRunOptions = this.pigRunOptions;
        });
        return;
      }
      const needle = val.toLowerCase();
      update(() => {
        this.filteredPigRunOptions = this.pigRunOptions.filter((option: any) =>
          option.label.toLowerCase().includes(needle)
        );
      });
    },
    filterCompletedRuns(val: string, update: (fn: () => void) => void) {
      if (val === '') {
        update(() => {
          this.filteredCompletedRunOptions = this.completedRunOptions;
        });
        return;
      }
      const needle = val.toLowerCase();
      update(() => {
        this.filteredCompletedRunOptions = this.completedRunOptions.filter(
          (option: any) => option.label.toLowerCase().includes(needle)
        );
      });
    },

    // --- Pig Run Select/Preview ---
    onPreviewPigRun() {
      this.selectedPigReport = '';
      this.$store.commit('SET_SELECTED_COMPLETED_PIG_RUN_ID', null);
      this.showInstructions = false;
      this.showAvailablePigs();
      if (this.selectedPigRun && this.selectedPigRun.value) {
        this.plotPigRunPreview(this.selectedPigRun.value);
      } else {
        this.removePreview();
      }
    },
    onClearPigRun() {
      this.selectedPigRun = '';
      this.removePreview();
    },
    async onPreviewCompletedPigRun() {
      this.completedPigRunBroadcastCommandButtonDisableLocal = false;
      this.$store.commit(
        'SET_SELECTED_COMPLETED_PIG_RUN_ID',
        this.selectedPigReport?.value ?? null
      );
      if (this.selectedPigReport) {
        this.pigRunSectionExpanded = false;
        this.plotPigRunPreview(this.selectedPigReport.value);
      } else {
        this.pigRunSectionExpanded = true;
        this.removePreview();
      }
    },
    onClearCompletedPigRun() {
      this.selectedPigReport = '';
      this.$store.commit('SET_SELECTED_COMPLETED_PIG_RUN_ID', null);
      this.pigRunSectionExpanded = true;
      this.removePreview();
    },
    refreshPreviewAfterVerification() {
      const pigRunId =
        this.selectedPigReport?.value || this.selectedPigRun?.value;
      if (pigRunId) {
        this.plotPigRunPreview(pigRunId, true);
      }
    },
    async plotPigRunPreview(pigRunId: number, skipZoom = false) {
      if (!this.map) return;
      this.removePreview();
      this.$store.commit('SET_PLOTTED_PIG_RUN_ID', pigRunId);
      const generation = this.previewGeneration;
      try {
        const response = await PigRunService.getPigRun(pigRunId);
        if (generation !== this.previewGeneration) return;
        const runData = response.data;
        this.$store.commit('SET_PLOTTED_PIG_RUN_DATA', runData);

        let markers = runData.markers;

        // For configured pig runs that don't have markers directly,
        // fetch them from the pipeline section instead.
        if ((!markers || markers.length === 0) && runData.pipelineSectionId) {
          const psResponse = await PigRunService.getPipelineSectionMarkers(
            runData.pipelineSectionId
          );
          if (generation !== this.previewGeneration) return;
          markers = psResponse.data.markers || [];
        }

        if (!markers || markers.length === 0) return;

        const sortedMarkers = [...markers].sort(
          (a: any, b: any) => a.sortOrder - b.sortOrder
        );
        this.previewSortedMarkers = sortedMarkers;

        // Build polyline from marker positions
        const latLngs: L.LatLng[] = sortedMarkers
          .filter((m: any) => m.latitude && m.longitude)
          .map((m: any) => L.latLng(m.latitude, m.longitude));

        if (latLngs.length >= 2) {
          this.pigRunPreview = markRaw(
            L.polyline(latLngs, {
              color: '#1976D2',
              weight: 3,
              opacity: 0.8,
            }).addTo(this.map)
          );
        }

        // Add marker icons and 100m circles
        for (const marker of sortedMarkers) {
          if (!marker.latitude || !marker.longitude) continue;

          const markerLatLng = L.latLng(marker.latitude, marker.longitude);

          const circle = L.circle(markerLatLng, {
            radius: 100,
            color: '#2196F3',
            fillColor: '#2196F3',
            fillOpacity: 0.1,
            weight: 1,
          }).addTo(this.map);
          this.previewCircles.push(markRaw(circle));

          let markerIcon: L.Icon | L.DivIcon;
          if (marker.description === 'LAUNCH') {
            markerIcon = PigRunCommon.launchIcon;
          } else if (marker.description === 'RECEIVE') {
            markerIcon = PigRunCommon.recieveIcon;
          } else if (marker.trackState === 'S') {
            markerIcon = PigRunCommon.skipIcon;
          } else {
            markerIcon = marker.hasVerifiedPassage
              ? PigRunCommon.trackIcon
              : PigRunCommon.trackIcon_2;
          }

          const leafletMarker = L.marker(markerLatLng, {
            icon: markerIcon,
          }).addTo(this.map);

          leafletMarker.bindTooltip(escapeHtml(marker.name), {
            permanent: false,
            direction: 'top',
            offset: [0, -8],
          });

          leafletMarker.on('click', () => {
            this.$store.dispatch('setSelectedMarker', {
              pigRunId: runData.pigRunId,
              pipelineSectionMarkerId: marker.pipelineSectionMarkerId,
              name: marker.name,
              lat: marker.latitude,
              lon: marker.longitude,
              trackState: marker.trackState,
              description: marker.description,
            });
          });

          this.agmMapMarkers.push(markRaw(leafletMarker));
        }

        // Zoom to fit the pig run markers
        if (latLngs.length > 0 && !skipZoom) {
          setTimeout(() => {
            this.map.invalidateSize();
            this.map.fitBounds(L.latLngBounds(latLngs), {
              maxZoom: 15,
              padding: [50, 50],
            });
          }, 100);
        }
      } catch (error) {
        console.error('Failed to fetch pig run for preview:', error);
      }
    },
    showAvailablePigs() {
      this.selectedPig = '';
      this.disableLaunchLocal = true;
    },

    // --- New / Edit Pig Run (delegated to wizard) ---
    onNewPigRun() {
      (this.$refs.pigRunWizard as any).openNew();
    },
    async onEditPigRun() {
      if (!this.selectedPigRun) return;
      (this.$refs.pigRunWizard as any).openEdit(this.selectedPigRun.value);
    },
    onWizardSaved() {
      this.selectedPigRun = '';
      this.fetchPigRunsList();
    },

    // --- Export CSV ---
    async onExportPigRun() {
      if (!this.selectedPigRun?.value || this.isExportingCsv) return;

      this.isExportingCsv = true;
      try {
        let markers: any[] = [];

        if (
          this.$store.state.plottedPigRunId === this.selectedPigRun.value &&
          this.previewSortedMarkers.length > 0
        ) {
          markers = this.previewSortedMarkers;
        } else {
          const response = await PigRunService.getPigRun(
            this.selectedPigRun.value
          );
          markers = response.data.markers ?? [];
          if (!markers.length && response.data.pipelineSectionId) {
            const psResponse = await PigRunService.getPipelineSectionMarkers(
              response.data.pipelineSectionId
            );
            markers = psResponse.data.markers ?? [];
          }
        }

        const csv = buildPipelineImportCsv(markers);
        const filename = `${sanitizeCsvFilename(
          this.selectedPigRun.label || 'pipeline'
        )}.csv`;
        triggerCsvDownload(csv, filename);
      } catch (exception) {
        console.error('onExportPigRun exception:', exception);
        this.$q.notify({
          type: 'negative',
          message: 'Failed to export CSV.',
        });
      } finally {
        this.isExportingCsv = false;
      }
    },

    // --- Delete Pig Run ---
    onDeletePigRun() {
      this.$q
        .dialog({
          title: 'Confirm',
          message: 'Confirm delete of pig run',
          persistent: true,
          ok: { color: 'grey-8' },
          cancel: { color: 'grey-8' },
        })
        .onOk(async () => {
          this.removePreview();
          try {
            await PigRunService.cancelPigRun(this.selectedPigRun.value);
            this.pigRunsList = this.pigRunsList.filter(
              (pr: ActivePigRun) => pr.pigRunId !== this.selectedPigRun.value
            );
            this.selectedPigRun = '';
            this.directionIsBackward = false;
          } catch (exception) {
            console.error('onDeletePigRun exception:', exception);
          }
        });
    },

    // --- Launch Pig Run ---
    async onLaunchPigRun() {
      this.launchPigToolType = '';
      this.launchPigToolName = '';
      this.directionIsBackward = false;
      this.disableLaunchLocal = true;
      this.showAdvancedLaunchOptions = false;
      this.launchPigExpectedSpeed = null;
      this.launchPigSpeedUnit = 'mph';
      this.LaunchTimestamp = dayjs
        .default()
        .utc()
        .format('YYYY-MM-DD HH:mm:ss');
      this.ErrorMsg = false;
      try {
        const response = await PigRunService.getAllToolTypes();
        this.launchPigToolTypeArray = response.data.map((t: any) => ({
          label: t.description,
          value: t.toolType,
        }));
      } catch (exception) {
        console.error(exception);
        this.launchPigToolTypeArray = [];
      }
      this.launchPigDialog = true;
    },
    toolNameRule(val: string): boolean | string {
      if (!val || val.trim().length === 0) {
        return 'Tool Name is required';
      }
      if (val.trim().length > 100) {
        return 'Tool Name must be 100 characters or fewer';
      }
      return true;
    },
    onReadyLaunch() {
      const toolNameValid =
        typeof this.launchPigToolName === 'string' &&
        this.launchPigToolName.trim().length > 0 &&
        this.launchPigToolName.trim().length <= 100;

      const toolTypeValid =
        typeof this.launchPigToolType === 'string' &&
        this.launchPigToolType.length > 0;

      this.disableLaunchLocal = !(toolNameValid && toolTypeValid);
    },
    onRunBackwardsChanged(val: boolean) {
      if (val) {
        this.showBackwardsConfirm = true;
      }
    },
    onBackwardsConfirmed() {
      this.showBackwardsConfirm = false;
    },
    onBackwardsCancelled() {
      this.directionIsBackward = false;
      this.showBackwardsConfirm = false;
    },
    async onAddLaunchTime() {
      if (!this.LaunchTimestamp) {
        this.ErrorMsg = true;
        this.ErrorMsgString = 'Error ! Invalid format.';
        return;
      }

      const acttime = this.LaunchTimestamp;
      const year = acttime.substring(0, 4);
      const month = acttime.substring(5, 7);
      const day = acttime.substring(8, 10);
      const hour = acttime.substring(11, 13);
      const min = acttime.substring(14, 16);
      const sec = acttime.substring(17, 19);

      const LaunchDate = new Date();
      LaunchDate.setUTCFullYear(parseInt(year));
      LaunchDate.setUTCMonth(parseInt(month) - 1);
      LaunchDate.setUTCDate(parseInt(day));
      LaunchDate.setUTCHours(parseInt(hour));
      LaunchDate.setUTCMinutes(parseInt(min));
      LaunchDate.setUTCSeconds(parseInt(sec));
      LaunchDate.setUTCMilliseconds(0);

      const NowDate = new Date();
      const diff = NowDate.getTime() - LaunchDate.getTime();

      if (diff < 0) {
        this.ErrorMsg = true;
        this.ErrorMsgString = 'Error ! Launch can not be set to future time.';
      } else {
        const request: LaunchLiveTrackPigRunRequest = {
          pigRunId: this.selectedPigRun.value,
          toolType: this.launchPigToolType,
          toolName: this.launchPigToolName.trim(),
          launchDate: this.LaunchTimestamp.replace(' ', 'T'),
          isBackwards: this.directionIsBackward,
          ...(this.launchPigExpectedSpeed != null &&
          this.launchPigExpectedSpeed > 0
            ? {
                expectedSpeed:
                  this.launchPigSpeedUnit === 'mph'
                    ? this.launchPigExpectedSpeed * 0.44704
                    : this.launchPigExpectedSpeed * (1 / 3.6),
              }
            : {}),
        };

        try {
          const response = await PigRunService.launchLiveTrackPigRun(request);

          const markers = response.data.markers || [];
          const lv = markers.find((m: any) => m.description === 'LAUNCH');

          const launchPayload: LaunchPigRunPayload = {
            response: response.data,
            toolName: request.toolName,
            toolType: request.toolType,
            launchDate: request.launchDate,
          };
          this.$store.commit('LAUNCH_PIG_RUN', launchPayload);
          this.$store.dispatch('selectPigRunLayer', response.data.pigRunId);

          this.$q.notify({
            type: 'positive',
            message: 'Pig run launched successfully.',
          });

          if (lv && lv.trackState === 'T') {
            this.pendingLaunchMarkerId = lv.pipelineSectionMarkerId;
            this.pendingLaunchPigRunId = response.data.pigRunId;
            this.launchMarkerPassageTimestamp = this.LaunchTimestamp;
            this.launchMarkerPassagePromptDialog = true;
          }
        } catch (error: any) {
          console.error('Failed to launch pig run', error);
          const msg =
            error?.response?.status === 400 && error?.response?.data
              ? String(error.response.data)
              : 'Failed to launch pig run.';
          this.$q.notify({
            type: 'negative',
            message: msg,
          });
          return;
        }

        this.launchPigDialog = false;

        this.selectedPigRun = '';
        this.selectedPig = '';
        this.directionIsBackward = false;
        this.removePreview();

        await this.fetchPigRunsList();
        await this.$store.dispatch('getActivePigRuns');
      }
    },

    // --- Reconstruct ---
    onWizardReconstruct() {
      this.reconstructLaunchDate = '';
      this.reconstructReceiveDate = '';
      this.reconstructDialog = true;
    },
    async onReconstructConfirm() {
      if (!this.selectedPigRun || !this.selectedPigRun.value) {
        return;
      }
      this.isReconstructing = true;
      try {
        const request: ReconstructPigRunRequest = {
          pigRunId: this.selectedPigRun.value,
          launchDate: this.reconstructLaunchDate.replace(' ', 'T'),
          receiveDate: this.reconstructReceiveDate.replace(' ', 'T'),
        };
        await PigRunService.reconstructPigRun(request);
        this.reconstructDialog = false;
        this.$q.notify({
          color: 'positive',
          message: 'Pig run reconstructed successfully.',
        });
        this.selectedPigRun = '';
      } catch (error) {
        const err = error as any;
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'An unexpected error occurred.';
        this.$q.notify({
          color: 'negative',
          message: `Failed to reconstruct pig run: ${message}`,
        });
      } finally {
        this.isReconstructing = false;
      }
    },

    // --- Completed Run Reports ---
    onOpenReportForCompletedRun() {
      if (!this.selectedPigReport || !this.selectedPigReport.value) return;
      this.$store.dispatch('showReportManager', {
        pigRunId: this.selectedPigReport.value,
      });
    },
    async onSynchRun() {
      this.$store.commit('SET_REPORT_SYNCH_STATUS', true);
      try {
        await PigRunService.synchFinishedPigRun(this.selectedPigReport.value);
        await this.fetchPigRunsList();
      } catch (error) {
        console.error('Failed to sync pig run:', error);
      } finally {
        this.$store.commit('SET_REPORT_SYNCH_STATUS', false);
      }
    },
    onDeleteReport() {
      this.$q
        .dialog({
          title: 'Confirm',
          message: 'Confirm delete of pig report',
          persistent: true,
          ok: { color: 'grey-8' },
          cancel: { color: 'grey-8' },
        })
        .onOk(async () => {
          try {
            await PigRunService.archivePigRun(this.selectedPigReport.value);
          } catch (error) {
            console.error('Failed to delete pig report:', error);
          }
          this.selectedPigReport = '';
          this.clearCompletedRun();
          await this.fetchPigRunsList();
        });
    },

    // --- Edit Time ---
    parseUtcTimestamp(value: string | Date | null | undefined): Date | null {
      if (!value) return null;
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
      }

      const timestamp = value.trim();
      const hasTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(timestamp);
      const isoTimestamp = hasTimeZone
        ? timestamp
        : `${timestamp.replace(' ', 'T')}Z`;
      const dateValue = new Date(isoTimestamp);

      return Number.isNaN(dateValue.getTime()) ? null : dateValue;
    },
    onEditTime() {
      this.EditTimeDialog = true;
      this.ErrorMsg = false;

      const launchTime = this.parseUtcTimestamp(
        this.selectedPigReport.runData.launchDate
      );
      const receiveTime = this.parseUtcTimestamp(
        this.selectedPigReport.runData.receiveDate
      );

      if (launchTime) {
        this.LaunchCompTimestamp = dayjs
          .default(launchTime)
          .utc()
          .format('YYYY-MM-DD HH:mm:ss');
      } else {
        this.LaunchCompTimestamp = '';
      }
      if (receiveTime) {
        this.ReceiveCompTimestamp = dayjs
          .default(receiveTime)
          .utc()
          .format('YYYY-MM-DD HH:mm:ss');
      } else {
        this.ReceiveCompTimestamp = '';
      }
    },
    async onEditCompTime() {
      const launchTimestamp = this.LaunchCompTimestamp.trim();
      const receiveTimestamp = this.ReceiveCompTimestamp.trim();

      if (!launchTimestamp) {
        this.ErrorMsg = true;
        this.ErrorMsgString = 'Error ! Launch Time is Empty.';
        return;
      }
      if (!receiveTimestamp) {
        this.ErrorMsg = true;
        this.ErrorMsgString = 'Error ! Receive Time is Empty.';
        return;
      }

      const LaunchDate = new Date(launchTimestamp + 'Z');
      if (isNaN(LaunchDate.getTime())) {
        this.ErrorMsgString =
          'Please enter valid Launch Time {YYYY-MM-DD HH:MM:SS}';
        this.ErrorMsg = true;
        return;
      }

      const ReceiveDate = new Date(receiveTimestamp + 'Z');
      if (isNaN(ReceiveDate.getTime())) {
        this.ErrorMsgString =
          'Please enter valid Receive Time {YYYY-MM-DD HH:MM:SS}';
        this.ErrorMsg = true;
        return;
      }

      const NowDate = new Date();

      if (LaunchDate.getTime() - NowDate.getTime() > 0) {
        this.ErrorMsg = true;
        this.ErrorMsgString = 'Error ! Launch Time entered is in the Future.';
        return;
      }

      if (ReceiveDate.getTime() - NowDate.getTime() > 0) {
        this.ErrorMsg = true;
        this.ErrorMsgString = 'Error ! Receive Time entered is in the Future.';
        return;
      }

      if (LaunchDate.getTime() >= ReceiveDate.getTime()) {
        this.ErrorMsg = true;
        this.ErrorMsgString = 'Error ! Receive Time must be after Launch Time.';
        return;
      }

      const pigRunId = this.selectedPigReport?.value;
      if (!pigRunId) {
        this.ErrorMsg = true;
        this.ErrorMsgString = 'Error ! A completed pig run must be selected.';
        return;
      }

      this.ErrorMsg = false;
      this.isUpdatingCompletedRunTime = true;

      try {
        await PigRunService.updateLaunchAndReceiveDates(
          pigRunId,
          LaunchDate.toISOString(),
          ReceiveDate.toISOString()
        );

        await this.fetchPigRunsList();
        const refreshedPigRun = this.completedRunOptions.find(
          (option: any) => option.value === pigRunId
        );
        if (refreshedPigRun) {
          this.selectedPigReport = refreshedPigRun;
        }

        await this.plotPigRunPreview(pigRunId, true);
        const pigRunSummary = this.$refs.pigRunSummary as
          | { refresh: () => Promise<void> }
          | undefined;
        await pigRunSummary?.refresh();
        this.EditTimeDialog = false;
      } catch (error: any) {
        this.ErrorMsg = true;
        this.ErrorMsgString =
          typeof error?.response?.data === 'string'
            ? error.response.data
            : 'Failed to update launch and receive times.';
      } finally {
        this.isUpdatingCompletedRunTime = false;
      }
    },

    // --- Add AGMs to Completed Run ---
    async onAddAgmsDialog() {
      if (!this.selectedPigReport) return;

      let existingUids: number[] = [];
      try {
        const response = await PigRunService.getPigRun(
          this.selectedPigReport.value
        );
        const fullRunData = response.data;
        existingUids = fullRunData.devices
          ? fullRunData.devices.map((d: any) => d.uid)
          : [];
      } catch (error) {
        console.error('Failed to fetch pig run details for AGMs:', error);
      }

      const existingSet = new Set(existingUids);
      this.addAgmOptions = [];
      this.addAgmSelected = [];
      this.addAgmSearch = '';
      this.addAgmRangeFrom = '';
      this.addAgmRangeTo = '';

      this.$store.state.device.devices.forEach((device: Device) => {
        if (!existingSet.has(device.uid)) {
          this.addAgmOptions.push({
            label: 'AGM UID ' + device.uid,
            value: device.uid,
          });
        }
      });

      this.addAgmOptions.sort((a, b) => a.value - b.value);
      this.addAgmsDialog = true;
    },
    onAddAgmSelectRange() {
      const from = parseInt(this.addAgmRangeFrom, 10);
      const to = parseInt(this.addAgmRangeTo, 10);
      if (isNaN(from) || isNaN(to)) return;
      for (const agm of this.filteredAddAgmOptions) {
        if (
          agm.value >= from &&
          agm.value <= to &&
          !this.addAgmSelected.includes(agm.value)
        ) {
          this.addAgmSelected.push(agm.value);
        }
      }
      this.$nextTick(() => {
        const list = (this.$refs.addAgmList as any)?.$el as
          | HTMLElement
          | undefined;
        if (!list) return;
        const firstIndex = this.filteredAddAgmOptions.findIndex(
          (a) => a.value >= from
        );
        if (firstIndex < 0) return;
        const items = list.querySelectorAll('.q-item');
        if (items[firstIndex]) {
          items[firstIndex].scrollIntoView({ block: 'nearest' });
        }
      });
    },
    onToggleSelectAllAddAgms() {
      if (this.allAddAgmsSelected) {
        const visibleValues = new Set(
          this.filteredAddAgmOptions.map((a) => a.value)
        );
        this.addAgmSelected = this.addAgmSelected.filter(
          (v) => !visibleValues.has(v)
        );
      } else {
        const existing = new Set(this.addAgmSelected);
        for (const agm of this.filteredAddAgmOptions) {
          if (!existing.has(agm.value)) {
            this.addAgmSelected.push(agm.value);
          }
        }
      }
    },
    async onAgmListChanged() {
      this.addAgmsDialog = false;

      if (this.addAgmSelected.length === 0 || !this.selectedPigReport) {
        this.addAgmOptions = [];
        this.addAgmSelected = [];
        return;
      }

      try {
        await PigRunService.addDevices(
          this.selectedPigReport.value,
          this.addAgmSelected
        );
        this.$q.notify({
          type: 'positive',
          message: `Added ${this.addAgmSelected.length} AGM(s) to the pig run.`,
        });
      } catch (error) {
        console.error('Failed to add devices:', error);
        this.$q.notify({
          type: 'negative',
          message: 'Failed to add AGMs to the pig run.',
        });
      }

      this.addAgmOptions = [];
      this.addAgmSelected = [];
    },
    onAgmListCancelled() {
      this.addAgmOptions = [];
      this.addAgmSelected = [];
    },

    // --- Broadcast Commands (Completed Run) ---
    completedPigRunBroadcastCommandButtonOnClick() {
      this.displayCompletedPigRunBroadcastCommandDialog = true;
    },
    async completedPigRunBroadcastCommandBroadcastNowOnClick() {
      const commandsToBroadcast: string[] = [];
      if (this.completedPigRunBroadcastCommandDownloadBetweenLaunchAndReceive) {
        commandsToBroadcast.push('RFD');
      }

      const completedPigRunBroadcastCommand =
        new CompletedPigRunBroadcastCommand(
          this.selectedPigReport.runData.pigRunId,
          commandsToBroadcast
        );

      await PigRunService.createCompletedPigRunBroadcastCommands(
        completedPigRunBroadcastCommand
      );

      this.displayCompletedPigRunBroadcastCommandDialog = false;
    },

    async fetchPigRunsList() {
      if (this.isPigRunsListLoading) return;
      this.isPigRunsListLoading = true;
      try {
        const response = await PigRunService.getPigRuns();
        this.pigRunsList = response.data;
        this.filteredCompletedRunOptions = this.completedRunOptions;
        this.filteredPigRunOptions = this.pigRunOptions;
      } catch (error) {
        console.error('Failed to fetch pig runs list:', error);
      } finally {
        this.isPigRunsListLoading = false;
      }
    },

    onOpenPipelineSectionManager() {
      this.managePipelineSectionDialog = true;
      this.showInstructions = false;
    },

    // --- Helpers ---
    truncateLabel(label: string, maxLen = 30): string {
      if (!label) return '';
      return label.length > maxLen ? label.slice(0, maxLen) + '…' : label;
    },

    // --- Popup Proxy Helpers ---
    hideDateProxy() {
      hidePopupProxy(this, 'qDateProxy');
    },
    hideTimeProxy() {
      hidePopupProxy(this, 'qTimeProxy');
    },
    hideReconstructLaunchDateProxy() {
      hidePopupProxy(this, 'reconstructLaunchDateProxy');
    },
    hideReconstructLaunchTimeProxy() {
      hidePopupProxy(this, 'reconstructLaunchTimeProxy');
    },
    hideReconstructReceiveDateProxy() {
      hidePopupProxy(this, 'reconstructReceiveDateProxy');
    },
    hideReconstructReceiveTimeProxy() {
      hidePopupProxy(this, 'reconstructReceiveTimeProxy');
    },
    hideLaunchPassageDateProxy() {
      hidePopupProxy(this, 'launchPassageDateProxy');
    },
    hideLaunchPassageTimeProxy() {
      hidePopupProxy(this, 'launchPassageTimeProxy');
    },

    // --- Launch Marker Passage Handlers ---
    async onAddLaunchMarkerPassage() {
      if (!this.pendingLaunchMarkerId || !this.launchMarkerPassageTimestamp) {
        return;
      }
      const passageDate = new Date(
        this.launchMarkerPassageTimestamp.replace(' ', 'T') + 'Z'
      );
      if (isNaN(passageDate.getTime())) {
        this.launchMarkerPassageErrorMsg = true;
        this.launchMarkerPassageErrorMsgString = 'Invalid date format.';
        return;
      }
      if (passageDate.getTime() > Date.now()) {
        this.launchMarkerPassageErrorMsg = true;
        this.launchMarkerPassageErrorMsgString =
          'Passage time cannot be in the future.';
        return;
      }
      this.isAddingLaunchPassage = true;
      try {
        const request = new AddManualPassageRequest(
          this.pendingLaunchMarkerId,
          passageDate
        );
        await PigRunService.addManualPassage(request);
        if (this.pendingLaunchPigRunId != null) {
          this.$store.dispatch('refreshSinglePigRun', {
            pigRunId: this.pendingLaunchPigRunId,
            force: true,
          });
        }
        this.launchMarkerPassagePromptDialog = false;
        this.pendingLaunchMarkerId = null;
        this.pendingLaunchPigRunId = null;
        this.$q.notify({
          type: 'positive',
          message: 'Manual passage added to launch marker.',
        });
      } catch (error) {
        console.error('Failed to add manual passage at launch marker:', error);
        this.$q.notify({
          type: 'negative',
          message: 'Failed to add manual passage.',
        });
      } finally {
        this.isAddingLaunchPassage = false;
      }
    },
  },
});
</script>
